/*
 * Copyright (c) 2021.
 * 2iPartners SàRL and/or its affiliates.
 * All rights reserved.
 *
 * 2iPartners SàRL
 * Route de Compois 24,
 * 1252 Meinier
 * Suisse
 *
 * tel : +41 22 735 97 07
 */

import {Component, Inject, LOCALE_ID, OnInit} from '@angular/core';
import {MAT_MOMENT_DATE_FORMATS, MomentDateAdapter} from '@angular/material-moment-adapter';
import {DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE} from '@angular/material/core';
import {ToastrService} from 'ngx-toastr';
import {AbstractControl, FormBuilder, FormGroup, Validators} from '@angular/forms';
import {Router} from '@angular/router';
import {Animal} from '../../_models/animal';
import {AnimalService} from '../../_services/animal.service';
import {HttpErrorResponse, HttpResponse} from '@angular/common/http';
import {Helpers} from '../../helpers';
import moment from "moment";
import {Moment} from 'moment';
import {PrintService} from '../../_services/print.service';
import {saveAs} from 'file-saver';
import {DATE_FORMAT} from '../../_enums/constants';
import {DEFAULT, EMBEDDED} from '../../_enums/serializer-groups';
import {HISTORY, I18nService} from '../../_services/i18n.service';


@Component({
  selector: 'app-history',
  templateUrl: './history.component.html',
  providers: [
    {provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE]},
    {provide: MAT_DATE_FORMATS, useValue: MAT_MOMENT_DATE_FORMATS}
  ]
})
export class HistoryComponent implements OnInit {

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                              FORMS GROUPS                                                                                          ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  public historyFG: FormGroup;

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                              PROPERTIES                                                                                            ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  public animals: Animal[];

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                              MAGIC METHODS                                                                                         ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  constructor(
    @Inject(LOCALE_ID) public locale: string,
    private animalService: AnimalService,
    private formBuilder: FormBuilder,
    private i18n: I18nService,
    private printService: PrintService,
    private router: Router,
    private toastr: ToastrService
  ) {
  }

  ngOnInit() {
    moment.locale(this.locale);
    this.initHistoryFG();
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                              ANIMAL FORM CONTROLS                                                                                  ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  get dateEndFC(): AbstractControl {
    return this.historyFG.get('dateEnd');
  }

  get dateStartFC(): AbstractControl {
    return this.historyFG.get('dateStart');
  }

  get thirdPartyFC(): AbstractControl {
    return this.historyFG.get('thirdParty');
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                              VALUES GETTERS                                                                                        ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  get dateEnd(): string {
    return this.dateEndFC && this.dateEndFC.value ? this.dateEndFC.value.format(DATE_FORMAT) : '';
  }

  get dateStart(): string {
    return this.dateStartFC && this.dateStartFC.value ? this.dateStartFC.value.format(DATE_FORMAT) : '';
  }

  get thirdPartyId(): number | null {
    return this.thirdPartyFC && this.thirdPartyFC.value ? Number(this.thirdPartyFC.value) : null;
  }

  get today(): Moment {
    return moment();
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                              FORMS INITIALISATION                                                                                  ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  private initHistoryFG() {
    this.historyFG = this.formBuilder.group({
      dateEnd: [{value: moment(), disabled: true}, Validators.required],
      dateStart: [{value: moment().subtract(6, 'month'), disabled: true}, Validators.required],
      thirdParty: [null, Validators.required]
    });
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                                     LISTENER                                                                                       ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  public printHistory(): void {
    Helpers.setLoading(true);

    this.printService.printThirdPartyHistory(this.thirdPartyId, this.dateStart, this.dateEnd, {version: 0.6}).subscribe(data => {
      Helpers.setLoading(false);
      saveAs(data, `${this.i18n.get(HISTORY)}_${this.thirdPartyId}_${this.dateStart}_${this.dateEnd}.pdf`);
    }, (err: HttpErrorResponse) => this.handleError(err));
  }


  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                              FORMS SUBMITS                                                                                         ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  public historyFormSubmit() {
    if (this.historyFG.valid) {
      Helpers.setLoading(true);

      this.animalService.findHistory(this.thirdPartyId, this.dateStart, this.dateEnd, {
        serializerGroups: [DEFAULT, EMBEDDED],
        version: 1.0
      }).subscribe((res: HttpResponse<Animal[]>) => {
        Helpers.setLoading(false);
        this.animals = res.body;
      }, (err: HttpErrorResponse) => this.handleError(err));
    }
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                              PRIVATE METHODS                                                                                       ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  private handleError(err: HttpErrorResponse): void {
    Helpers.setLoading(false);
    this.toastr.error(`${err.status} - ${this.i18n.get(err.status)}`);
  }

}
